import * as React from 'react';
import { observer } from 'mobx-react';
import { StyledSlider } from '~views/dashboard/SliderWrapper';
import { ISliderSettings } from '@estee/elc-slider';
import { IBenefit, ITierBenefits } from './ITierBenefits';
import { toFloat } from '~utils/CaseUtils';
import styled from 'styled-components';
import { classes } from '~utils/Classes';
import { marketingPageSections as ms } from '~views/marketing-page/sections';
import { Breakpoints } from '@estee/elc-base-theme';
import { Tab, Tabs } from '@estee/elc-tabs';
import { AnalyticalTrackingUseCase } from '~use-cases/AnalyticalTrackingUseCase';
import { eventTag } from '~utils/Analytics';

export const TEST_ID_TIER_BENEFITS = 'loyalty-marketing-tier-benefits';
export const TEST_ID_TIER_BENEFITS_ITEM = 'loyalty-marketing-tier-benefits-item';
const tierBenefitsWrapperClass = classes('tier-benefits-wrapper');
const headingSectionClass = classes('tier-benefits-heading');
const headingWrapperClass = classes('tier-benefits-header');
const subHeadingWrapperClass = classes('tier-benefits-subheader');
const tierBenefitsSliderClass = classes('tier-benefits-slider');
const benefitItemClass = classes('tier-benefits-item');
const benefitItemImageClass = classes('tier-benefits-item-image');
const benefitItemDetailsClass = classes('tier-benefits-item-details');
const benefitItemTitleClass = classes('tier-benefits-item-details-title');
const benefitItemDescriptionClass = classes('tier-benefits-item-details-description');

const TierBenefitsWrapper = styled.div<{ backgroundColor: string; color: string }>`
    grid-area: ${ms.tierBenefits};
    background-color: ${({ backgroundColor }) => `#${backgroundColor}`};
    color: ${({ color }) => `#${color}`};
`;
const TierBenefitsWrapperInner = styled.div``;
const HeadingSection = styled.div``;
const HeadingWrapper = styled.div``;
const SubheadingWrapper = styled.div``;
const SliderWrapper = styled.div`
    grid-area: tierBenefitsSliderWrapper;
`;
const ItemWrapper = styled.div<{ color: string }>`
    color: ${({ color }) => `#${color}`};
`;

const BenefitImage = styled.div``;
const BenefitTitle = styled.div``;
const BenefitDescription = styled.div``;
const TabsWrapper = styled.div``;
const TabHeader = styled(Tabs)<{ color: string; inactiveColor: string }>`
    display: block;

    .elc-tabs__tab > div {
        color: ${({ inactiveColor }) => `#${inactiveColor}`};
    }

    .elc-tabs__tab--current > div {
        color: ${({ color }) => `#${color}`};
    }
`;

const TabContent = styled(Tab)`
    display: block;
`;
const tabsWrapperClass = classes('tier-benefits-tabs-wrapper');
const tierBenefitsWrapperInner = classes('tier-benefits-wrapper-inner');

export const BenefitItem = observer(function BenefitItem({
    benefitImage: { alt, src },
    key,
    title,
    description,
    cardTextColor
}: IBenefit & { cardTextColor: string }) {
    return (
        <ItemWrapper
            key={`${key}-image`}
            className={benefitItemClass}
            data-test-id={TEST_ID_TIER_BENEFITS_ITEM}
            color={cardTextColor}
        >
            <BenefitImage className={benefitItemImageClass}>
                <img src={src} alt={alt} className={benefitItemImageClass} />
            </BenefitImage>
            <div className={benefitItemDetailsClass}>
                <BenefitTitle className={benefitItemTitleClass}>{title}</BenefitTitle>
                <BenefitDescription className={benefitItemDescriptionClass}>
                    {description}
                </BenefitDescription>
            </div>
        </ItemWrapper>
    );
});

export const TierBenefits = observer(function TierBenefitsSection({ tierBenefits }: ITierBenefits) {
    const {
        header,
        subHeader,
        tierBenefitsBackgroundColor,
        color,
        inactiveTabsColor,
        cardTextColor,
        slider,
        noOfSliderCards,
        noOfCardsMovement,
        tiers,
        disableTabs
    } = tierBenefits;
    const sliderCount = toFloat(noOfSliderCards?.text) || 4;
    const cardMovementCount = toFloat(noOfCardsMovement?.text) || 4;
    const [currentTabId, setCurrentTabId] = React.useState<string>('1');
    const onArrowClick = (eLabel: string) => {
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_benefits', event_action: 'arrow_click', event_label: eLabel },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    };

    const benefitItems = tiers?.map(({ key, name }) => {
        const filteredBenefits = slider
            .filter((benefit) => benefit.tiers.includes(key))
            .map((benefitItem: IBenefit) => {
                return <BenefitItem {...benefitItem} cardTextColor={cardTextColor.hex} />;
            });

        const infinitySlider = filteredBenefits.length >= sliderCount;
        const sliderInfiniteSettings: ISliderSettings = {
            dots: false,
            arrows: true,
            slidesToShow: sliderCount,
            slidesToScroll: cardMovementCount,
            accessibility: true,
            infinite: infinitySlider,
            beforeChange: (oldIndex, newIndex) => { (newIndex < oldIndex) ? onArrowClick('right') : onArrowClick('left'); },
            responsive: [
                {
                    breakpoint: Breakpoints.largeScreen,
                    settings: {
                        slidesToShow: sliderCount > 3 ? 3 : sliderCount,
                        slidesToScroll: cardMovementCount > 3 ? 3 : cardMovementCount
                    }
                },
                {
                    breakpoint: 980,
                    settings: {
                        slidesToShow: sliderCount > 2 ? 2 : sliderCount,
                        slidesToScroll: cardMovementCount > 2 ? 2 : cardMovementCount
                    }
                },
                {
                    breakpoint: 600,
                    settings: {
                        slidesToShow: sliderCount > 2 ? 1.5 : 1,
                        slidesToScroll: cardMovementCount,
                        centerMode: false,
                        dots: true,
                        arrows: false
                    }
                }
            ],
            lazyload: true
        };

        return disableTabs ? (
            <SliderWrapper>
                <StyledSlider
                    className={tierBenefitsSliderClass}
                    items={filteredBenefits}
                    sliderSettings={sliderInfiniteSettings}
                />
            </SliderWrapper>
        ) : (
            <TabContent key={key} id={`${key}`} title={name}>
                <SliderWrapper>
                    <StyledSlider
                        className={tierBenefitsSliderClass}
                        items={filteredBenefits}
                        sliderSettings={sliderInfiniteSettings}
                    />
                </SliderWrapper>
            </TabContent>
        );
    });

    return (
        <TierBenefitsWrapper
            backgroundColor={tierBenefitsBackgroundColor.hex}
            color={color.hex}
            className={tierBenefitsWrapperClass}
            data-test-id={TEST_ID_TIER_BENEFITS}
        >
            <TierBenefitsWrapperInner className={tierBenefitsWrapperInner}>
                <HeadingSection className={headingSectionClass}>
                    <HeadingWrapper className={headingWrapperClass}>{header}</HeadingWrapper>
                    <SubheadingWrapper
                        className={subHeadingWrapperClass}
                        dangerouslySetInnerHTML={{ __html: subHeader }}
                    />
                </HeadingSection>
                <TabsWrapper className={tabsWrapperClass}>
                    {disableTabs ? (
                        benefitItems
                    ) : (
                        <TabHeader
                            currentTabId={currentTabId}
                            onSelect={setCurrentTabId}
                            inactiveColor={inactiveTabsColor.hex}
                            color={color.hex}
                        >
                            {benefitItems}
                        </TabHeader>
                    )}
                </TabsWrapper>
            </TierBenefitsWrapperInner>
        </TierBenefitsWrapper>
    );
});
